/**
 * @fileoverview countdownClock — the ONE reading of time left before a deadline, and time since a stamp
 * @package @puffin-core/countdown
 * @module countdownClock
 * @description A countdown on a screen is a read of a clock AGAINST A DEADLINE, never a number decremented once a
 *   tick: a tab the browser throttles, or a device that sleeps, runs its timers slow, so a decremented countdown says
 *   more time is left than is (POS-STATION-IDLE-CLOCK-001 prompt 2).
 *
 *   Reading the wall clock alone is not enough either. Each clock is wrong in one case:
 *   - the WALL clock (`Date.now()`) is right across sleep, and wrong when someone sets the device clock: moved back, it
 *     says more time is left;
 *   - a MONOTONIC clock (`performance.now()`) is right across a clock change, and may pause while the device sleeps:
 *     then it says more time is left.
 *
 *   Both errors say MORE time is left, so the reading takes the SMALLER remainder of the two, and the larger elapsed
 *   time of the two. Neither reading is ever generous; a clock moved FORWARD makes the wall clock read short, which is
 *   the safe side.
 *
 *   Both readings are measured from an ANCHOR: the two clocks read together when the deadline (or stamp) was first
 *   seen. A new deadline takes a new anchor.
 */

/**
 * The two clocks, read together.
 *
 * @typedef {{wallMs: number, monoMs: number}} ClockReading
 */

/**
 * Read both clocks now.
 *
 * @returns {ClockReading}
 */
export function readClocks() {
  return { wallMs: Date.now(), monoMs: performance.now() };
}

/**
 * Milliseconds left until `deadlineMs`, never below zero: the smaller of the wall clock's and the monotonic clock's
 * remainders, both measured from `anchor`.
 *
 * @param {number} deadlineMs - The deadline, epoch milliseconds.
 * @param {ClockReading} anchor - Both clocks, read when the deadline was first seen.
 * @param {ClockReading} now - Both clocks, read now.
 * @returns {number}
 */
export function remainingMsOf(deadlineMs, anchor, now) {
  const byWall = deadlineMs - now.wallMs;
  const byMono = deadlineMs - anchor.wallMs - (now.monoMs - anchor.monoMs);
  return Math.max(0, Math.min(byWall, byMono));
}

/**
 * Milliseconds since `stampMs`, never below zero: the larger of the wall clock's and the monotonic clock's elapsed
 * times, both measured from `anchor`.
 *
 * @param {number} stampMs - The stamp, epoch milliseconds.
 * @param {ClockReading} anchor - Both clocks, read when the stamp was first seen.
 * @param {ClockReading} now - Both clocks, read now.
 * @returns {number}
 */
export function elapsedMsOf(stampMs, anchor, now) {
  const byWall = now.wallMs - stampMs;
  const byMono = anchor.wallMs - stampMs + (now.monoMs - anchor.monoMs);
  return Math.max(0, byWall, byMono);
}
